import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import Switch from "react-switch";
import { Input } from "reactstrap";

import { templateStore, templateActions } from "../../templateStore";
import { SWITCH_PROPS, FORM_KEYS, EMAIL_CONFIG_BASE_PATH } from "./constants";
import { isEmailValid } from "@helpers/general";

export function FormEmails() {
    const { t } = useTranslation();
    const snap = useSnapshot(templateStore);

    const rawEmailConfig = snap.template?.SiteWeb?.Forms?.EmailConfig;
    const emailConfig = useMemo(() => rawEmailConfig || {}, [rawEmailConfig]);
    const useGlobalEmail = emailConfig.UseGlobalEmail ?? true;
    const storeGlobalEmail = emailConfig.GlobalEmail ?? "";

    const getFormEmail = useCallback(
        (formKey: string): string => {
            return emailConfig[formKey]?.Email ?? "";
        },
        [emailConfig]
    );

    const [localGlobalEmail, setLocalGlobalEmail] = useState(storeGlobalEmail);
    const [localFormEmails, setLocalFormEmails] = useState<
        Record<string, string>
    >({});
    const [isFocused, setIsFocused] = useState<string | null>(null);

    useEffect(() => {
        if (isFocused !== "global") {
            setLocalGlobalEmail(storeGlobalEmail);
        }
    }, [storeGlobalEmail, isFocused]);

    useEffect(() => {
        FORM_KEYS.forEach((form) => {
            if (isFocused !== form.key) {
                setLocalFormEmails((prev) => ({
                    ...prev,
                    [form.key]: getFormEmail(form.key),
                }));
            }
        });
    }, [getFormEmail, isFocused]);

    const handleToggleGlobal = (checked: boolean) => {
        templateActions.updateTemplate(
            [...EMAIL_CONFIG_BASE_PATH, "UseGlobalEmail"],
            checked
        );
    };

    const handleGlobalEmailChange = (value: string) => {
        const trimmed = value.trim();
        setLocalGlobalEmail(trimmed);
        templateActions.updateTemplate([...EMAIL_CONFIG_BASE_PATH, "GlobalEmail"], trimmed);
    };

    const handleFormEmailChange = (formKey: string, value: string) => {
        const trimmed = value.trim();
        setLocalFormEmails((prev) => ({ ...prev, [formKey]: trimmed }));
        templateActions.updateTemplate([...EMAIL_CONFIG_BASE_PATH, formKey, "Email"], trimmed);
    };

    return (
        <div
            className="p-4"
            data-testid="form-emails-container"
            style={{ maxWidth: "800px" }}
        >
            <div
                className="mb-4"
                data-testid="form-emails-title"
                style={{
                    font: "normal normal 600 20px/27px Segoe UI",
                }}
            >
                {t("Form email configuration")}
            </div>
            <div
                className="mb-4"
                style={{
                    font: "normal normal normal 14px/20px Segoe UI",
                    color: "#6c757d",
                }}
            >
                {t(
                    "Configure the email addresses where form submissions will be sent"
                )}
            </div>

            {/* Toggle: email global vs spécifique */}
            <div
                className="d-flex align-items-center mb-4 p-3"
                style={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    gap: "12px",
                }}
            >
                <div data-testid="global-email-switch">
                    <Switch
                        {...SWITCH_PROPS}
                        aria-label={t("Use a single email for all forms")}
                        checked={useGlobalEmail}
                        onChange={handleToggleGlobal}
                    />
                </div>
                <div>
                    <div
                        style={{
                            font: "normal normal 600 16px/22px Segoe UI",
                        }}
                    >
                        {t("Use a single email for all forms")}
                    </div>
                    <div
                        style={{
                            font: "normal normal normal 13px/18px Segoe UI",
                            color: "#6c757d",
                        }}
                    >
                        {useGlobalEmail
                            ? t(
                                  "All form submissions will be sent to the same email address"
                              )
                            : t(
                                  "Each form can have its own destination email address"
                              )}
                    </div>
                </div>
            </div>

            {useGlobalEmail ? (
                /* Email global unique */
                <div className="mb-3">
                    <label
                        htmlFor="global-email-input"
                        className="mb-2 d-block"
                        style={{
                            font: "normal normal 600 15px/20px Segoe UI",
                        }}
                    >
                        {t("Destination email for all forms")}
                        <span style={{ color: "#E30613" }}> *</span>
                    </label>
                    <Input
                        id="global-email-input"
                        data-testid="global-email-input"
                        type="email"
                        value={localGlobalEmail}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleGlobalEmailChange(e.target.value)
                        }
                        onFocus={() => setIsFocused("global")}
                        onBlur={() => setIsFocused(null)}
                        placeholder="contact@example.com"
                        style={{ width: "100%" }}
                    />
                    {localGlobalEmail && !isEmailValid(localGlobalEmail) && (
                        <div
                            role="alert"
                            aria-live="polite"
                            className="mt-1"
                            style={{ color: "#E30613", fontSize: "12px" }}
                        >
                            {t("Please enter a valid email address")}
                        </div>
                    )}
                </div>
            ) : (
                /* Un email par formulaire */
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                    }}
                >
                    {FORM_KEYS.map((form) => {
                        const email =
                            localFormEmails[form.key] ?? getFormEmail(form.key);
                        return (
                            <div
                                key={form.key}
                                data-testid={`form-email-card-${form.key}`}
                                className="p-3"
                                style={{
                                    border: "1px solid #dee2e6",
                                    borderRadius: "8px",
                                }}
                            >
                                <label
                                    htmlFor={`form-email-input-${form.key}`}
                                    className="mb-2 d-block"
                                    style={{
                                        font:
                                            "normal normal 600 15px/20px Segoe UI",
                                    }}
                                >
                                    {t(form.labelKey)}
                                    <span style={{ color: "#E30613" }}> *</span>
                                </label>
                                <Input
                                    id={`form-email-input-${form.key}`}
                                    data-testid={`form-email-input-${form.key}`}
                                    type="email"
                                    value={email}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                    ) =>
                                        handleFormEmailChange(
                                            form.key,
                                            e.target.value
                                        )
                                    }
                                    onFocus={() => setIsFocused(form.key)}
                                    onBlur={() => setIsFocused(null)}
                                    placeholder={`${t(
                                        form.labelKey
                                    ).toLowerCase()}@example.com`}
                                    style={{ width: "100%" }}
                                />
                                {email && !isEmailValid(email) && (
                                    <div
                                        role="alert"
                                        aria-live="polite"
                                        className="mt-1"
                                        style={{
                                            color: "#E30613",
                                            fontSize: "12px",
                                        }}
                                    >
                                        {t(
                                            "Please enter a valid email address"
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
